import { useMemo } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../constants';
import { useDebouncedValue } from '../../../../hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../schema/hannibal/HannibalContext';
import {
  CombinedSearchSuggestionsDocument,
  SuggestionCombinedFragment,
} from '../../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../../util/EMPTY_LIST';
import { parseToEnum } from '../../../../util/parseToEnum';
import { CountryState } from '../../constants';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import { TabProps } from '../TabProps';
import { ALWAYS_ONLINE_BOOKED_TOPICS } from '../constants';
import { SuggestionData } from '../types';

export const mapCombinedSuggestionsToSuggestionData = (
  suggestions: readonly SuggestionCombinedFragment[],
  includeText = true,
) =>
  suggestions.flatMap<SuggestionData>(it => {
    if (it.text && includeText) {
      return [
        {
          type: 'TextSuggestion',
          id: `text-${it.text.value}`,
          filters: { text: it.text.value },
        },
      ];
    }

    if (it.locationSpecialty?.suburb) {
      const suburb = it.locationSpecialty.suburb;

      const specialist = it.locationSpecialty.specialty.specialist;
      return [
        {
          type: 'FilterSuggestion',
          id: `locationSpecialty-${it.locationSpecialty.specialty.specialist}-${suburb.postcode}-${suburb.name}`,
          filters: {
            topic: { specialist },
            onlineAppointments: ALWAYS_ONLINE_BOOKED_TOPICS.includes(specialist) ? {} : undefined,
            location: {
              suburb: {
                state: parseToEnum(suburb.state, CountryState) ?? undefined,
                postcode: suburb.postcode,
                name: suburb.name,
              },
            },
          },
        },
      ];
    }

    return [];
  });

export const FilterSuggestions = ({ onChoose, searchTerm, trackingData }: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const { data } = useHannibalQuery(CombinedSearchSuggestionsDocument, { variables: { query } });

  const specialties = data?.searchSuggestions.suggestions ?? EMPTY_LIST;

  const suggestions: SuggestionData[] = useMemo(
    () => mapCombinedSuggestionsToSuggestionData(specialties, true),
    [specialties],
  );

  return (
    <SuggestionList
      onChoose={onChoose}
      suggestions={suggestions}
      trackingData={trackingData}
      searchTerm={searchTerm}
    />
  );
};
